import { PaginationState } from '@tanstack/react-table';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CreateButton, MediaPlayer, Tabs, TabsType } from '../../components';
import { ACTIVE_TAB_PARAM, ACTIVE_TAB_TO_CALL_KIND, PAGINATION_PAGE_SIZE } from '../../constants';
import { useAppDispatch, useAppSelector, useFeatureFlag, useGetFiltersFromParams } from '../../hooks';
import { closeMediaPlayer, setCalls } from '../../redux/reducers';
import { useGetCallsMutation } from '../../services';
import {
  CallSortingFilters,
  CustomSortingState,
  LD_FeatureFlags,
  RealCallsFilterKeys,
  ReviewFilters,
  ReviewTab as ReviewTabType,
  SortingOrder,
} from '../../types';
import { kebabCaseToLabel } from '../../utils';
import CallDetailsDrawer from './CallDetailsDrawer';
import ImportRealCallsModal from './ImportRealCallsModal';
import ReviewPageContent from './ReviewPageContent';

const ReviewPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTabFromParams = searchParams.get(ACTIVE_TAB_PARAM);
  const activeTab = currentTabFromParams as ReviewTabType;

  // State for pagination settings.
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGINATION_PAGE_SIZE,
  });
  // State for sorting settings.
  const [sorting, setSorting] = useState<CustomSortingState>({
    sortBy: CallSortingFilters.START_TIME,
    sortOrder: SortingOrder.DESC,
  });

  const [isImportRealCallsModalOpen, setIsImportRealCallsModalOpen] = useState(false);

  // Redux
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);

  // Mutations
  const [getCalls, { data, isLoading }] = useGetCallsMutation();
  const totalPages = data?.pagination.totalPages || 0;

  // Ref to the media player element.
  const mediaPlayerRef = useRef<HTMLDivElement | null>(null);

  const filters = useGetFiltersFromParams() as ReviewFilters;
  const realCallsFF = useFeatureFlag(LD_FeatureFlags.RELEASE_REAL_CALLS);

  const tabs = [
    { id: ReviewTabType.SIMULATIONS, title: kebabCaseToLabel(ReviewTabType.SIMULATIONS) },
    { id: ReviewTabType.REAL_CALLS, title: kebabCaseToLabel(ReviewTabType.REAL_CALLS), disabled: !realCallsFF },
  ];

  // Reset to page 1 when filters change.
  useEffect(() => {
    if (pagination.pageIndex !== 0) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [filters, setPagination]);

  const fetchCalls = useCallback(() => {
    let filteredFilters = { ...filters };

    // Remove simulation-specific filters when on real calls tab
    if (activeTab === ReviewTabType.REAL_CALLS) {
      filteredFilters = Object.fromEntries(
        Object.entries(filters).filter(([key]) =>
          Object.values(RealCallsFilterKeys).includes(key as RealCallsFilterKeys)
        )
      );
    }

    getCalls({
      ...Object.entries(filteredFilters).reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {}),
      startTime: filteredFilters.startTime ? [filteredFilters.startTime] : undefined,
      pagination: {
        pageIndex: pagination.pageIndex + 1, // DataTable uses 0-based page indexing, so we need to add 1 to the page index.
        pageSize: pagination.pageSize,
      },
      sorting,
      callKind: ACTIVE_TAB_TO_CALL_KIND[activeTab],
    });
  }, [filters, pagination, sorting, activeTab, getCalls]);

  // Fetch calls whenever filters or pagination settings change.
  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  // This effect sets the list of calls for the Review Page in state.
  // It allows for optimistic updates to the state instead of refetching from the server.
  useEffect(() => {
    if (isLoading) return;

    dispatch(setCalls(data?.calls || []));

    return () => {
      dispatch(setCalls([]));
    };
  }, [data?.calls, isLoading, dispatch]);

  // Reset the call details when the user navigates away from the Review page.
  useEffect(() => {
    return () => {
      dispatch(closeMediaPlayer());
    };
  }, [dispatch]);

  if (!user) return null;

  return (
    <>
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between py-4 pl-8 pr-4">
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            type={TabsType.BORDERED}
            setActiveTab={(newActiveTab) => setSearchParams({ [ACTIVE_TAB_PARAM]: newActiveTab })}
          />
          {activeTab === ReviewTabType.SIMULATIONS && <CreateButton />}
          {activeTab === ReviewTabType.REAL_CALLS && (
            <ImportRealCallsModal isOpen={isImportRealCallsModalOpen} setIsOpen={setIsImportRealCallsModalOpen} />
          )}
        </div>
        <ReviewPageContent
          sorting={sorting}
          setSorting={setSorting}
          pagination={pagination}
          setPagination={setPagination}
          isLoading={isLoading}
          filters={filters}
          totalPages={totalPages}
          setIsImportRealCallsModalOpen={setIsImportRealCallsModalOpen}
        />
      </div>

      {/* Call Details Drawer */}
      <CallDetailsDrawer mediaPlayerRef={mediaPlayerRef} />
      {/* MediaPlayer Drawer */}
      <MediaPlayer ref={mediaPlayerRef} />
    </>
  );
};

export default ReviewPage;
